import React from 'react';
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
} from '@mui/material';
import { StyledTextField } from '../productInformation';
import { useTranslation } from 'react-i18next';
import { useProductFieldError } from '../validation';
import { buildSelectInputWithInfo } from '../fieldInfo';
import { ComplianceField } from 'src/Lca/compliance';

interface Props {
  values: {
    regulatory_frameworks: string;
    goals: {
      intended_applications: string;
      study_reasons: string;
      target_audience: string;
      comissioner_name: string;
      external_experts_involved: boolean;
      external_experts_description: string;
      results_disclosed: boolean;
      assertions_disclosed: boolean;
      results_confidential: boolean;
      assertions_confidential: boolean;
      disclosure_description: string;
      comment: string;
    };
  };
  setValues: Function;
}

const disclosureGroups = [
  {
    key: 'results',
    labelKey: 'resultsDisclosure',
    disclosedKey: 'results_disclosed',
    disclosedLabelKey: 'resultsDisclosed',
    confidentialKey: 'results_confidential',
    confidentialLabelKey: 'resultsConfidential',
  },
  {
    key: 'comparative_assertions',
    labelKey: 'comparativeAssertionsDisclosure',
    disclosedKey: 'assertions_disclosed',
    disclosedLabelKey: 'comparativeResultsDisclosed',
    confidentialKey: 'assertions_confidential',
    confidentialLabelKey: 'comparativeAssertionsConfidential',
  },
] as const;

export default function Assertions({ setValues, values }: Props) {
  const { t, i18n } = useTranslation('lca', { keyPrefix: 'assertions' });
  const disclosureDescriptionError = useProductFieldError(
    'disclosure_description',
    values.goals.disclosure_description
  );
  const handle_disclosure_description = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setValues({
      ...values,
      goals: {
        ...values.goals,
        disclosure_description: e.target.value,
      },
    });
  };

  const getDisclosureValue = (
    disclosedKey: 'results_disclosed' | 'assertions_disclosed',
    confidentialKey: 'results_confidential' | 'assertions_confidential'
  ) => {
    const isDisclosed = values.goals[disclosedKey];
    const isConfidential = values.goals[confidentialKey];
    if (isDisclosed && !isConfidential) return 'disclosed';
    if (isConfidential && !isDisclosed) return 'confidential';
    return '';
  };

  const handleDisclosureSelection =
    (
      disclosedKey: 'results_disclosed' | 'assertions_disclosed',
      confidentialKey: 'results_confidential' | 'assertions_confidential'
    ) =>
    (e: SelectChangeEvent<string>) => {
      const { value } = e.target;
      setValues({
        ...values,
        goals: {
          ...values.goals,
          [disclosedKey]: value === 'disclosed',
          [confidentialKey]: value === 'confidential',
        },
      });
    };

  return (
    <div
      style={{
        display: 'flex',
        gap: '24px',
        flexDirection: 'column',
      }}
    >
      {disclosureGroups.map((group) => (
        <ComplianceField
          key={group.key}
          excelRow={group.key === 'results' ? 112 : 113}
          regulatoryFrameworks={values.regulatory_frameworks}
        >
          <FormControl fullWidth>
            <InputLabel>{t(group.labelKey)}</InputLabel>
            <Select
              value={getDisclosureValue(
                group.disclosedKey,
                group.confidentialKey
              )}
              label={t(group.labelKey)}
              input={buildSelectInputWithInfo({
                label: t(group.labelKey),
                title: i18n.t('lca:fieldInfoFallback', {
                  field: t(group.labelKey),
                }),
              })}
              onChange={handleDisclosureSelection(
                group.disclosedKey,
                group.confidentialKey
              )}
            >
              <MenuItem value="">{t('selectOption')}</MenuItem>
              <MenuItem value="disclosed">
                {t(group.disclosedLabelKey)}
              </MenuItem>
              <MenuItem value="confidential">
                {t(group.confidentialLabelKey)}
              </MenuItem>
            </Select>
          </FormControl>
        </ComplianceField>
      ))}
      <StyledTextField
        fullWidth
        label={t('otherDisclosureConditions')}
        name="disclosure_description"
        value={values.goals.disclosure_description}
        onChange={handle_disclosure_description}
        multiline
        rows={3}
        error={!!disclosureDescriptionError}
        helperText={disclosureDescriptionError}
      />
    </div>
  );
}
